import React, { useEffect, useRef, useState } from "react";
import { TitleBar } from "../../Components/Common/TitleBar/titleBar";
import {
  Button,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Spinner,
  Table,
  Toast,
  ToastBody,
  ToastHeader,
} from "reactstrap";
import "./style.scss";

import { TripModal } from "../../Components/Common/TripModal/tripModal";
import DeleteModal from "../../Components/Common/DeleteModal";
import { Pagination } from "../../Components/Common/Pagination";
import { useDispatch, useSelector } from "react-redux";

import {
  createUser,
  deleteUser,
  getAllUsersList,
  getUserById,
  updateUser,
} from "../../slices/userManagement/thunk";
import { getAllRoleList } from "../../slices/rolesAndPermission/thunk";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  hideErrorMessage,
  setLoading,
} from "../../slices/userManagement/reducer";
import "react-phone-input-2/lib/style.css";
import PhoneNumberField from "../../Components/Common/PhoneNumberField";
import { removeCountryCode } from "../../constant/utils";

const UserManagement = () => {
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const getRoles = useSelector((state) => state.Roles.rolesList);
  const usersList = useSelector((state) => state.UsersManagement.usersList);
  const userById = useSelector((state) => state.UsersManagement.userById);
  const loading = useSelector((state) => state.UsersManagement.loading);

  const [viewDetails, setViewDetails] = useState(false);
  const [addUserModal, setAddUserModal] = useState(false);
  const [deleteUserModal, setDeleteUserModal] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [toasterType, setToasterType] = useState("");

  const toggleViewDetailModal = () => {
    setViewDetails(!viewDetails);
  };

  const toggleAddUserModal = async () => {
    setAddUserModal(!addUserModal);
  };

  const toggleDeleteUserModal = async () => {
    setDeleteUserModal(!deleteUserModal);
  };

  const usersManagementSchema = Yup.object().shape({
    first_name: Yup.string()
      .trim()
      .min(1, "First Name is required")
      .required("First Name is required"),
    last_name: Yup.string()
      .trim()
      .min(1, "Last Name is required")
      .required("Last Name is required"),
    email: Yup.string().email("Email is invalid").required("Email is required"),
    role: Yup.string().required("Role is required"),
    phone_number: Yup.string()
      .matches(/^\d{10,15}$/, "Phone Number must be valid")
      .required("Phone Number is required"),
    is_active: Yup.boolean().required("is_active is required"),
  });

  const addInitialValues = {
    first_name: "",
    email: "",
    last_name: "",
    role: "",
    phone_number: "",
    country_code: "",
    is_active: true,
  };

  const updateInitialValues = {
    first_name: userById?.first_name,
    email: userById?.email,
    last_name: userById?.last_name,
    role: userById?.role,
    phone_number: `${userById?.phone_number}`,
    country_code: userById?.country_code,
    is_active: userById?.is_active,
  };

  const showToaster = (isSuccess, message) => {
    setShowToast(true);
    setToasterType(isSuccess ? "success" : "danger");
    setToastMessage(message);
    setTimeout(() => {
      setShowToast(false);
      if (!isSuccess) {
        dispatch(hideErrorMessage());
      }
    }, 3000);
  };

  const handleUsersSubmit = async (values) => {
    try {
      let response;
      if (!isAdd) {
        response = await dispatch(updateUser(userById.id, values));
      } else {
        response = await dispatch(createUser(values));
      }
      const { success: isSuccess, message } = response;

      showToaster(isSuccess, message || "Operation successful");
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while processing your request"
      );
    } finally {
      toggleAddUserModal();
      await dispatch(getAllUsersList(currentPage, pageSize, searchText));
    }
  };
  const handleDeleteUser = async () => {
    try {
      const { success: isSuccess, message } = await dispatch(
        deleteUser(userById.id)
      );
      if (isSuccess) {
        showToaster(isSuccess, message || "User Successfully Deleted");
      }
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while deleting the user"
      );
    } finally {
      toggleDeleteUserModal();
      await dispatch(getAllUsersList(currentPage, pageSize, searchText));
    }
  };

  useEffect(() => {
    try {
      dispatch(getAllRoleList("None", "None", ""));
    } catch (error) {}
    const delayDebounceFn = setTimeout(() => {
      try {
        dispatch(getAllUsersList(currentPage, pageSize, searchText));
      } catch (error) {}
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [dispatch, currentPage, pageSize, searchText]);

  document.title = "User Management | D&A";
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex flex-column gap-4 ">
            <TitleBar title={"User Management"} />

            <div
              className="position-fixed top-0 end-0 p-3"
              style={{ zIndex: 1055 }}
            >
              {showToast && (
                <Toast isOpen={showToast}>
                  <ToastHeader
                    icon={"danger" === toasterType ? "danger" : "success"}
                    toggle={() => setShowToast(false)}
                  >
                    {"danger" === toasterType ? "Error" : "Success"}
                  </ToastHeader>
                  <ToastBody>{toastMessage}</ToastBody>
                </Toast>
              )}
            </div>

            <div className="userManagementSection">
              <div className="d-flex justify-content-end align-items-end p-2 p-lg-0 gap-1 gap-lg-0">
                <InputGroup className="search-input">
                  <InputGroupText>
                    <i className="ri-search-line" />
                  </InputGroupText>
                  <Input
                    type="text"
                    placeholder="Search input text"
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        dispatch(getAllUsersList(1, pageSize, searchText));
                        setCurrentPage(1);
                      }
                    }}
                  />
                </InputGroup>
                <Button
                  onClick={() => {
                    setIsAdd(true);
                    toggleAddUserModal();
                    dispatch(setLoading({ type: "read", value: false }));
                  }}
                  className="newUserManagementBtn d-flex gap-2"
                >
                  <span className="d-none d-lg-flex">Create User</span>
                  <i className="ri-add-line" />
                </Button>
              </div>

              <div className="trip-table">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Sr.No</th>
                      <th>User Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Phone Number</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  {loading.read ? (
                    <tbody>
                      <tr>
                        <td colSpan="9" className="text-center align-middle">
                          <Spinner color="primary" />
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody>
                      {usersList?.results?.length ? (
                        usersList?.results?.map((element, index) => (
                          <tr key={index}>
                            <td>{(currentPage - 1) * pageSize + index + 1}</td>
                            <td>
                              {element.first_name} {element.last_name}
                            </td>
                            <td>{element.email}</td>
                            <td>{element.role_name || "-"}</td>
                            <td>
                              {" "}
                              {removeCountryCode(
                                element.country_code,
                                element.phone_number
                              )}
                            </td>
                            <td>
                              {" "}
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "80px",
                                  textAlign: "center",
                                  backgroundColor: element.is_active
                                    ? "#28a74533"
                                    : "#dc354533",
                                  padding: "4px 8px",
                                  borderRadius: "10px",
                                  color: element.is_active
                                    ? "#28a745"
                                    : "#dc3545",
                                  fontWeight: 500,
                                }}
                              >
                                {element.is_active ? "Active" : "Inactive"}{" "}
                              </span>{" "}
                            </td>
                            <td>
                              <div className="action-icons">
                                <i
                                  className="ri-eye-fill icon"
                                  style={{ color: "#E8A014" }}
                                  onClick={() => {
                                    dispatch(getUserById(element.id));
                                    toggleViewDetailModal();
                                    dispatch(
                                      setLoading({ type: "read", value: false })
                                    );
                                  }}
                                />
                                <i
                                  className="ri-pencil-fill icon"
                                  style={{ color: "#007AFF" }}
                                  onClick={() => {
                                    dispatch(getUserById(element.id));
                                    setIsAdd(false);
                                    toggleAddUserModal();
                                    dispatch(
                                      setLoading({ type: "read", value: false })
                                    );
                                  }}
                                />
                                <i
                                  className="ri-delete-bin-fill icon"
                                  style={{ color: "#FB0000" }}
                                  onClick={() => {
                                    dispatch(getUserById(element.id));
                                    toggleDeleteUserModal();
                                    dispatch(
                                      setLoading({ type: "read", value: false })
                                    );
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="text-start text-sm-center" colSpan={9}>
                            <h3>No User Found</h3>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  )}
                </Table>
              </div>
              {usersList?.results?.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  totalItems={usersList?.count || 0}
                />
              )}
            </div>
          </div>

          <TripModal
            isModalOpen={viewDetails}
            toggleModal={toggleViewDetailModal}
            buttonText="Assign"
            closeText={"Close without saving"}
            title={"User Management"}
            tripText={"User Details"}
            showButtons={false}
            showDeleteIcon={false}
            showEditIcon={false}
            showcloseIcon={true}
            editIconClick={() => {
              toggleViewDetailModal();
              toggleAddUserModal();
              setIsAdd(false);
            }}
            deleteIconClick={() => {
              toggleViewDetailModal();
              toggleDeleteUserModal();
            }}
          >
            <div className="px-3 d-flex flex-column gap-4">
              <Row className="mb-5">
                <div className="custom-box-value">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      User Name<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue">
                      {" "}
                      {userById?.first_name} {userById?.last_name}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      Email<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue">{userById?.email}</span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      Role<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue">
                      {userById?.role_name}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value border-0 pb-0">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      Phone Number<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue">
                      {removeCountryCode(
                        userById?.country_code,
                        userById?.phone_number
                      )}
                    </span>
                  </div>
                </div>
              </Row>
            </div>
          </TripModal>

          <TripModal
            isModalOpen={addUserModal}
            toggleModal={toggleAddUserModal}
            buttonText={isAdd ? "Add" : "Update"}
            closeText={"Close without saving"}
            title={"User Management"}
            tripText={isAdd ? "Add User New" : "Update User"}
            showcloseIcon={true}
            size="lg"
            loading={isAdd ? loading.create : loading.update}
            onSubmit={() => {
              if (formikRef.current) {
                formikRef.current.handleSubmit();
              }
            }}
          >
            <div className="px-3 d-flex gap-4">
              <Formik
                innerRef={formikRef}
                enableReinitialize
                initialValues={isAdd ? addInitialValues : updateInitialValues}
                validationSchema={usersManagementSchema}
                onSubmit={handleUsersSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="first_name" className="passwordLabel">
                            First Name
                          </Label>
                          <Input
                            type="text"
                            name="first_name"
                            className="profileInput"
                            id="first_name"
                            placeholder="Enter First Name"
                            value={values.first_name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            invalid={
                              touched.first_name && errors.first_name
                                ? true
                                : false
                            }
                          />
                          {touched.first_name && errors.first_name ? (
                            <FormFeedback type="invalid">
                              {errors.first_name}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="last_name" className="passwordLabel">
                            Last Name
                          </Label>
                          <Input
                            type="text"
                            name="last_name"
                            className="profileInput"
                            id="last_name"
                            placeholder="Enter Last Name"
                            value={values.last_name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            invalid={
                              touched.last_name && errors.last_name
                                ? true
                                : false
                            }
                          />
                          {touched.last_name && errors.last_name ? (
                            <FormFeedback type="invalid">
                              {errors.last_name}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="email" className="passwordLabel">
                            Email
                          </Label>
                          <Input
                            type="text"
                            name="email"
                            className="profileInput"
                            id="email"
                            placeholder="Enter Email"
                            value={values.email}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            invalid={
                              touched.email && errors.email ? true : false
                            }
                          />
                          {touched.email && errors.email ? (
                            <FormFeedback type="invalid">
                              {errors.email}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="role" className="passwordLabel">
                            Select Role
                          </Label>
                          <Input
                            type="select"
                            name="role"
                            className="profileInput"
                            id="role"
                            value={values.role}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={touched.role && errors.role ? true : false}
                          >
                            <option value="">Select Role</option>
                            {Array.isArray(getRoles) && getRoles.length > 0 ? (
                              getRoles
                                .filter((role) => role.name !== "Driver")
                                .map((role) => (
                                  <option key={role.id} value={role.id}>
                                    {role.name}
                                  </option>
                                ))
                            ) : (
                              <option disabled>No roles found</option>
                            )}
                          </Input>
                          {touched.role && errors.role ? (
                            <FormFeedback type="invalid">
                              {errors.role}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <PhoneNumberField
                          label="Phone Number"
                          phoneValue={values.phone_number}
                          countryCode={updateInitialValues.country_code}
                          onChange={(phone, code) => {
                            formikRef.current.setFieldValue(
                              "phone_number",
                              phone
                            );
                            formikRef.current.setFieldValue(
                              "country_code",
                              code
                            );
                          }}
                          onBlur={handleBlur}
                        />
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="role" className="passwordLabel">
                            Status
                          </Label>
                          <Input
                            type="select"
                            name="is_active"
                            className="profileInput"
                            id="is_active"
                            value={values.is_active}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={
                              touched.is_active && errors.is_active
                                ? true
                                : false
                            }
                          >
                            <option value={true}>Active</option>
                            <option value={false}>Inactive</option>
                          </Input>
                          {touched.is_active && errors.is_active ? (
                            <FormFeedback type="invalid">
                              {errors.is_active}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </div>
          </TripModal>

          <DeleteModal
            show={deleteUserModal}
            loading={loading.delete}
            onDeleteClick={() => {
              handleDeleteUser();
            }}
            onCloseClick={toggleDeleteUserModal}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserManagement;
